/**
 * Fetch wrapper for calling Next.js API routes from React Query
 * This is used by React Query hooks to call Next.js API routes
 * Automatically handles token refresh on 401 errors using a Subscriber Queue pattern
 */

import { logger } from "@/lib/logger";
import { currentPathForLogin, loginUrlFor } from "@/lib/utils/login-redirect";

export interface NextJsFetchOptions extends RequestInit {
  requireAuth?: boolean;
  redirectOnAuthError?: boolean;
}

// Queue to hold failed requests while token is refreshing
interface FailedRequest {
  resolve: (value: unknown) => void;
  reject: (reason?: unknown) => void;
}

let isRefreshing = false;
let failedQueue: FailedRequest[] = [];

// Process the queue of failed requests
const processQueue = (error: Error | null, tokenRefreshed: boolean = false) => {
  failedQueue.forEach((prom) => {
    if (error) {
      // Each waiter gets its OWN error (review #8 L2). fetchFromNextJs tags the
      // error it throws with that request's method, only when untagged; one
      // shared instance kept whichever method was tagged first, so every other
      // queued request was reported (and judged by the retry policy) as that one.
      prom.reject(errorForWaiter(error));
    } else {
      prom.resolve(tokenRefreshed);
    }
  });
  failedQueue = [];
};

/**
 * Hooks the identity layer (components/identity-sync.tsx) registers so this
 * module can report session changes without importing React Query.
 */
export interface SessionHooks {
  /**
   * True when this tab is rendering an IMPERSONATED identity. A 401 there means
   * the impersonation session ended (expired, stopped, superseded), and the
   * refresh that follows necessarily returns the ADMIN's token: the refresh
   * cookie is theirs. Replaying the failed request would silently run it as the
   * admin under a banner that still says "acting as X", so it is not replayed.
   */
  isImpersonating?: () => boolean;
  /** Called after every successful refresh; the identity may have changed. */
  onTokenRefreshed?: () => void;
  /**
   * Called when code OUTSIDE fetchFromNextJs saw the session refused in a way
   * that means the identity may no longer be what this tab shows (today: an
   * attachment upload answered 401 IMPERSONATION_ENDED). The identity layer
   * re-checks /users/me, which goes through fetchFromNextJs's own
   * refresh-without-replay path and resets the tab if it changed.
   */
  onIdentityInDoubt?: () => void;
}

let sessionHooks: SessionHooks = {};

/** Register the session hooks. Returns the unregister function. */
export function setSessionHooks(hooks: SessionHooks): () => void {
  sessionHooks = hooks;
  return () => {
    if (sessionHooks === hooks) sessionHooks = {};
  };
}

/** See SessionHooks.onIdentityInDoubt. A no-op when nothing is registered. */
export function reportIdentityInDoubt(): void {
  sessionHooks.onIdentityInDoubt?.();
}

/**
 * The error_code the backend answers on every route but /users/me and the
 * change-password one, while the user must set a new password.
 */
export const PASSWORD_CHANGE_REQUIRED = "PASSWORD_CHANGE_REQUIRED";

/**
 * Registered by the password guard (components/password-change-guard.tsx), so
 * this module can report the refusal without importing React or a router.
 *
 * Kept OUT of SessionHooks on purpose: setSessionHooks replaces the whole
 * object, and the identity layer already owns it — a second component calling
 * it would silently unregister the first.
 */
let passwordChangeRequiredHandler: (() => void) | null = null;

/** Register the handler. Returns the unregister function. */
export function setPasswordChangeRequiredHandler(handler: () => void): () => void {
  passwordChangeRequiredHandler = handler;
  return () => {
    if (passwordChangeRequiredHandler === handler) passwordChangeRequiredHandler = null;
  };
}

/**
 * Report that the server refused a request until the password is changed. A
 * no-op when nothing is registered; exported so non-fetch transports (an XHR
 * upload) can report it too.
 */
export function reportPasswordChangeRequired(): void {
  passwordChangeRequiredHandler?.();
}

export type ProactiveRefreshOutcome =
  /** This tab is impersonating: nothing was sent. */
  | "skipped-impersonating"
  /** 2xx. The identity layer has been asked to re-check. */
  | "refreshed"
  /** 401/403: there is no session to refresh into; the user must log in. */
  | "rejected"
  /** 429 or a server error, even after one more try: the session may well
   *  still be valid, so this is not a logout. */
  | "unavailable"
  /** The request itself failed (network), even after one more try; logged. */
  | "failed";

/**
 * A refresh made on a timer rather than in answer to a 401 (the SSE provider's
 * 13-minute keep-alive). Lives here so every browser-side refresh consults the
 * same session hooks.
 *
 * NOT WHILE IMPERSONATING. The refresh cookie is the ADMIN's, so refreshing
 * swaps a live impersonation token for the admin's while every banner still
 * says "acting as X": REST runs as the admin, unjournaled, until the countdown
 * or a focus re-check notices (review #4 M1). Skipping costs nothing: an
 * impersonation token cannot be renewed, its session ends at its own expiry.
 *
 * After a successful refresh the identity layer re-checks, exactly as after
 * fetchFromNextJs's own refresh, so any identity change it causes is found.
 */
export async function refreshSessionProactively(): Promise<ProactiveRefreshOutcome> {
  if (sessionHooks.isImpersonating?.()) return "skipped-impersonating";
  const outcome = await requestTokenRefresh();
  if (outcome === "ok") {
    sessionHooks.onTokenRefreshed?.();
    return "refreshed";
  }
  return outcome;
}

type ErrorWithStatus = Error & {
  httpStatus?: number;
  status?: number;
  httpMethod?: string;
  /** A code beside a plain message (see sessionRefreshUnavailableError). */
  errorCode?: string;
};

/**
 * The HTTP status of a response error thrown by fetchFromNextJs, when known.
 * The mutation retry policy (retry-policy.ts) reads it so a request the server
 * rejected is never resent.
 */
export function httpStatusOf(error: unknown): number | undefined {
  if (!(error instanceof Error)) return undefined;
  const status = (error as ErrorWithStatus).httpStatus;
  return typeof status === "number" ? status : undefined;
}

function withHttpStatus(error: Error, status: number): Error {
  (error as ErrorWithStatus).httpStatus = status;
  return error;
}

/**
 * The HTTP method of the request an error came from, when the error came from
 * fetchFromNextJs (every error it throws carries one). The retry policy needs
 * it: resending a POST after a server error or a dropped connection can
 * duplicate a write that already landed.
 */
export function httpMethodOf(error: unknown): string | undefined {
  if (!(error instanceof Error)) return undefined;
  const method = (error as ErrorWithStatus).httpMethod;
  return typeof method === "string" ? method : undefined;
}

function impersonationEndedError(): Error {
  const error: ErrorWithStatus = new Error(
    "AUTH_ERROR: Impersonation session ended - request not replayed as another user",
  );
  error.httpStatus = 401;
  // `status` IS set here, on purpose: this error is new, nothing else reads it,
  // and the mutation retry predicate must see a 4xx so it does not resend the
  // request, which would now run as the admin.
  error.status = 401;
  return error;
}

/** The error_code of the failure thrown when a 401's refresh could not be answered. */
export const SESSION_REFRESH_UNAVAILABLE = "SESSION_REFRESH_UNAVAILABLE";

/**
 * A request failed because the refresh its 401 needed could not be answered
 * (429, 5xx, network), even after one more try. Structured, with an error_code,
 * so handleApiError shows a translated message instead of this English text
 * (review #8 L3). A 503 without the AUTH_ERROR prefix, because the session may
 * be perfectly valid: this must be shown, not skipped as a redirect.
 */
function sessionRefreshUnavailableError(): Error {
  // Plain text, with the code carried beside it (review #10 L3): about 18
  // screens show `err.message` directly, and a JSON-shaped message rendered as
  // raw JSON there. handleApiError reads `errorCode` and shows the translation.
  const error = withHttpStatus(
    new Error("Session refresh is temporarily unavailable. Please try again."),
    503,
  ) as Error & { errorCode?: string };
  error.errorCode = SESSION_REFRESH_UNAVAILABLE;
  return error;
}

/**
 * One queued request's copy of the failure: same name, message and status, and
 * no method yet, so fetchFromNextJs tags it with that request's own method.
 */
function errorForWaiter(error: Error): Error {
  const copy: ErrorWithStatus = new Error(error.message);
  copy.name = error.name;
  const { httpStatus, status, errorCode } = error as ErrorWithStatus;
  if (httpStatus !== undefined) copy.httpStatus = httpStatus;
  if (status !== undefined) copy.status = status;
  if (errorCode !== undefined) copy.errorCode = errorCode;
  return copy;
}

type RefreshOutcome = "ok" | "rejected" | "unavailable" | "failed";

/** Pause before the second refresh attempt, when the response gave no Retry-After. */
export const REFRESH_RETRY_DELAY_MS = 1500;
/**
 * The longest a second attempt waits, whatever Retry-After says. The limiter
 * keys on the refresh-token VALUE, and a burst that hits it is usually a
 * wake-up whose rotated cookie lands within a second or two: by the time this
 * elapses the browser likely presents a new value with a fresh budget, and the
 * request that is waiting should not stall for the old window's full minute.
 */
export const REFRESH_RETRY_MAX_DELAY_MS = 5000;

const sleep = (ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms));

/**
 * The one browser-side call to /api/auth/refresh, shared by the 401 path in
 * fetchFromNextJs and by refreshSessionProactively.
 *
 * ONLY 401/403 MEANS THE SESSION IS GONE. A 429 (the refresh rate limit) or a
 * server error says nothing about the session, and reading every non-2xx as
 * "rejected" sent staff with valid sessions to the login page. A transient
 * outcome is tried once more after a pause: the 429's Retry-After (the backend's
 * per-session limiter sends it), capped, or a short default.
 */
async function requestTokenRefresh(): Promise<RefreshOutcome> {
  const first = await refreshOnce();
  if (first.outcome === "ok" || first.outcome === "rejected") return first.outcome;
  await sleep(first.retryAfterMs ?? REFRESH_RETRY_DELAY_MS);
  return (await refreshOnce()).outcome;
}

async function refreshOnce(): Promise<{ outcome: RefreshOutcome; retryAfterMs?: number }> {
  try {
    const refreshResponse = await fetch("/api/auth/refresh", {
      method: "POST",
      credentials: "include",
      headers: {
        "Content-Type": "application/json",
      },
    });
    if (refreshResponse.ok) return { outcome: "ok" };
    if (refreshResponse.status === 401 || refreshResponse.status === 403) {
      return { outcome: "rejected" };
    }
    const seconds = Number(refreshResponse.headers.get("Retry-After"));
    const retryAfterMs =
      Number.isFinite(seconds) && seconds > 0
        ? Math.min(seconds * 1000, REFRESH_RETRY_MAX_DELAY_MS)
        : undefined;
    return { outcome: "unavailable", retryAfterMs };
  } catch (error) {
    logger.error(error, "Error refreshing token");
    return { outcome: "failed" };
  }
}

// Get current branch ID from context/storage
function getCurrentBranchId(): number | null {
  if (typeof window === "undefined") return null;
  const saved = localStorage.getItem("novaerp_current_branch_id");
  if (saved) {
    const branchId = parseInt(saved, 10);
    return isNaN(branchId) ? null : branchId;
  }
  return null;
}

/**
 * Fetch a Next.js API route. Every error it throws carries the request's HTTP
 * method (httpMethodOf), and response errors their status (httpStatusOf), so
 * the mutation retry policy can tell a rejection from a transient failure, and
 * a create from an update.
 */
export async function fetchFromNextJs<T>(
  path: string,
  options: NextJsFetchOptions = {}
): Promise<T> {
  try {
    return await requestNextJs<T>(path, options);
  } catch (error) {
    if (error instanceof Error && (error as ErrorWithStatus).httpMethod === undefined) {
      (error as ErrorWithStatus).httpMethod = (options.method ?? "GET").toUpperCase();
    }
    throw error;
  }
}

async function requestNextJs<T>(
  path: string,
  options: NextJsFetchOptions = {}
): Promise<T> {
  const {
    requireAuth = true,
    redirectOnAuthError = true,
    ...fetchOptions
  } = options;

  // Build URL - use relative path for Next.js routes
  const url = path.startsWith("/") ? path : `/${path}`;

  // Skip refresh logic for the refresh endpoint itself to prevent infinite loops
  const isRefreshEndpoint = url.includes("/api/auth/refresh");

  // Get current branch ID and add to headers if available
  const branchId = getCurrentBranchId();
  const headers = new Headers(fetchOptions.headers);

  if (!headers.has("Content-Type")) {
    headers.set("Content-Type", "application/json");
  }

  // Add branch header if branch ID is available
  // Skip for call-center routes (global routes)
  if (branchId && !url.includes("/call-center")) {
    headers.set("X-Branch-ID", branchId.toString());
  }

  const response = await fetch(url, {
    ...fetchOptions,
    credentials: "include", // Important for cookies
    headers,
  });

  // Handle 401 errors with automatic token refresh
  if (response.status === 401 && !isRefreshEndpoint && requireAuth) {
    // Sampled before refreshing: once the refresh lands, the cookie is the
    // admin's whatever this tab is still showing.
    const wasImpersonating = sessionHooks.isImpersonating?.() ?? false;

    if (isRefreshing) {
      // If already refreshing, queue this request
      try {
        await new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        });
        // Retry the request after refresh completes
        return fetchFromNextJs<T>(path, options);
      } catch (err) {
        // If refresh failed, propagate failure
        if (redirectOnAuthError) {
          throw err;
        }
      }
    }

    isRefreshing = true;

    try {
      const refreshOutcome = await requestTokenRefresh();

      if (refreshOutcome === "ok") {
        // Whatever the tab believed, the identity may have changed; the
        // identity layer re-checks /users/me and resets the tab on a mismatch.
        sessionHooks.onTokenRefreshed?.();

        if (wasImpersonating) {
          // See SessionHooks.isImpersonating: the fresh token is the admin's,
          // so neither this request nor the queued ones are replayed. The
          // AUTH_ERROR prefix keeps handleApiError from toasting while the
          // identity reset reloads the tab.
          const err = impersonationEndedError();
          processQueue(err);
          // Returned, not thrown: a throw here would land in the catch below,
          // which sends the browser to the login page.
          return Promise.reject(err);
        }

        // Refresh succeeded, process queue and retry original request
        processQueue(null, true);

        // Retry original request
        return fetchFromNextJs<T>(path, options);
      } else if (refreshOutcome === "unavailable" || refreshOutcome === "failed") {
        // The refresh could not be answered (rate limit, a restarting backend,
        // the network), even after one more try. The session may be perfectly
        // valid, so this is NOT a logout: fail this request and the queued ones
        // and let the next request try again. Returned, not thrown: a throw here
        // would land in the catch below, which sends the browser to the login page.
        const err = sessionRefreshUnavailableError();
        processQueue(err);
        return Promise.reject(err);
      } else {
        // Refresh refused (401/403): the session is gone.
        const error = new Error("AUTH_ERROR: Session expired - redirecting to login");
        processQueue(error);

        if (redirectOnAuthError) {
          if (typeof window !== "undefined") {
            window.location.href = loginUrlFor(currentPathForLogin());
          }
          throw error;
        }
        throw new Error("Session expired. Please log in again.");
      }
    } catch (error) {
      // Refresh caused an exception
      const err = error instanceof Error ? error : new Error(String(error));
      processQueue(err);

      if (redirectOnAuthError) {
        if (typeof window !== "undefined") {
          window.location.href = loginUrlFor(currentPathForLogin());
        }
        throw new Error("AUTH_ERROR: Unexpected error during refresh");
      }
      throw err;
    } finally {
      isRefreshing = false;
    }
  }

  if (!response.ok) {
    const errorData = (await response.json().catch(() => ({
      error: "An error occurred while fetching the data",
    }))) as {
      error?: string;
      message?: string;
      field?: string;
      errorKey?: string;
      error_code?: string;
      suggestion?: string;
    };

    // Handle authentication errors (if not already handled above)
    if (response.status === 401 && redirectOnAuthError) {
      if (typeof window !== "undefined") {
        window.location.href = loginUrlFor(currentPathForLogin());
      }
      // Throw error with AUTH_ERROR prefix so components can detect and skip showing toast
      throw withHttpStatus(
        new Error("AUTH_ERROR: Unauthorized - redirecting to login"),
        response.status,
      );
    }

    // A tab whose cached /users/me still says nothing is required finds out
    // here: the server refuses everything until the password is changed. Report
    // it so the guard can move the tab to the change-password screen, then
    // throw as usual so the caller still sees a failed request.
    if (errorData.error_code === PASSWORD_CHANGE_REQUIRED) {
      reportPasswordChangeRequired();
    }

    // If error has field information, preserve it in JSON format
    if (errorData.field) {
      throw withHttpStatus(new Error(JSON.stringify(errorData)), response.status);
    }

    // Preserve structured backend errors (e.g. 24h-window guard) as JSON so
    // callers can branch on error_code instead of regex-matching message
    // text. Plain {error: "..."} responses fall through to the legacy
    // string-message path below.
    if (errorData.error_code) {
      throw withHttpStatus(new Error(JSON.stringify(errorData)), response.status);
    }

    throw withHttpStatus(
      new Error(
        errorData.error ||
        errorData.message ||
        `HTTP error! status: ${response.status}`
      ),
      response.status,
    );
  }

  // Handle 204 No Content responses (common for DELETE operations)
  if (response.status === 204 || response.status === 205) {
    return null as T;
  }

  // Check if response has content before trying to parse JSON
  const contentType = response.headers.get("content-type");
  const contentLength = response.headers.get("content-length");

  // If no content-type or content-length is 0, return null
  if (!contentType || contentLength === "0") {
    return null as T;
  }

  // Try to parse JSON, but handle empty responses gracefully
  try {
    const text = await response.text();
    if (!text || text.trim() === "") {
      return null as T;
    }
    // Handle cases where API returns "Data not found" or similar plain text
    if (contentType?.includes("text/plain")) {
      // Try to parse as JSON first in case content-type is wrong
      try {
        return JSON.parse(text) as T;
      } catch {
        // Return text wrapped in object if needed, or null if it's just a message
        return null as T;
      }
    }
    return JSON.parse(text) as T;
  } catch (error) {
    // If JSON parsing fails but response was OK, return null
    return null as T;
  }
}
